import { useCallback, useEffect, useState } from "react";
import { Link, Outlet, useParams } from "react-router-dom";
import { useAuth } from "../../../auth/useAuth";
import { ADMIN_PERMISSIONS } from "../../../auth/adminPermissions";
import RequirePermission from "../../../auth/RequirePermission";
import AdminPageLayout from "../../../components/midpoint/admin/layout/AdminPageLayout";
import AdminPageHeader from "../../../components/midpoint/admin/layout/AdminPageHeader";
import AdminStatePanel from "../../../components/midpoint/admin/layout/AdminStatePanel";
import {
  fetchAdminDealer,
  runAdminDealerAction,
  startAdminDealerPortalSession,
  trackDealerEvent,
} from "../../../services/automapDealerOnboardingApi";
import { AdminAutomotiveNav } from "../automotive/AdminAutomotiveNav";
import { AdminDealerNav, DealerDetailNav } from "./dealerAdminShared";

function AdminDealerLayoutContent() {
  const { dealerId } = useParams();
  const { hasPermission, isSuperAdmin } = useAuth();
  const canManage = isSuperAdmin || hasPermission(ADMIN_PERMISSIONS.REAL_ESTATE_SOURCES_MANAGE);

  const [dealer, setDealer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [portalOpening, setPortalOpening] = useState(false);

  const reload = useCallback(async () => {
    try {
      setLoading(true);
      setErrorMessage("");
      const data = await fetchAdminDealer(dealerId);
      setDealer(data.dealer);
    } catch (err) {
      setErrorMessage(err.message || "No se pudo cargar el concesionario.");
      setDealer(null);
    } finally {
      setLoading(false);
    }
  }, [dealerId]);

  useEffect(() => {
    reload();
  }, [reload]);

  const runAction = async (action, payload = {}) => {
    if (!canManage) return;
    try {
      await runAdminDealerAction(dealerId, action, payload);
      trackDealerEvent(`admin_dealer_${action}`, { dealerId });
      await reload();
    } catch (err) {
      setErrorMessage(err.message || "Acción fallida.");
    }
  };

  const handleViewAsDealer = async (dealerUserId) => {
    if (!canManage) return;
    try {
      setPortalOpening(true);
      setErrorMessage("");
      const result = await startAdminDealerPortalSession(
        dealerId,
        dealerUserId ? { dealerUserId } : {}
      );
      window.location.assign(result.redirectUrl || "/v/automap/dealer/dashboard");
    } catch (err) {
      setErrorMessage(err.message || "No se pudo abrir el portal del concesionario.");
      setPortalOpening(false);
    }
  };

  if (loading) {
    return (
      <AdminPageLayout>
        <AdminStatePanel variant="empty" message="Cargando concesionario…" />
      </AdminPageLayout>
    );
  }

  if (errorMessage && !dealer) {
    return (
      <AdminPageLayout>
        <AdminStatePanel variant="empty" message={errorMessage} onRetry={reload} retryLabel="Reintentar" />
      </AdminPageLayout>
    );
  }

  if (!dealer) {
    return (
      <AdminPageLayout>
        <AdminStatePanel variant="empty" message="Concesionario no encontrado." />
      </AdminPageLayout>
    );
  }

  return (
    <AdminPageLayout>
      <AdminAutomotiveNav active="dealers" />
      <AdminPageHeader
        eyebrow="AutoMapa · Concesionarios"
        title={dealer.businessName}
        description={`${dealer.city || "—"} · ${dealer.slug}${dealer.isReadOnly ? " · Solo lectura (archivado)" : ""}`}
        navigation={<AdminDealerNav active="list" />}
        primaryActions={
          <>
            {canManage ? (
              <button
                type="button"
                className="admin-btn admin-btn--primary"
                disabled={portalOpening}
                onClick={() => handleViewAsDealer()}
              >
                {portalOpening ? "Abriendo portal…" : "View as dealer"}
              </button>
            ) : null}
            <Link className="admin-btn admin-btn--secondary" to={`/admin/dealers/${dealerId}/edit`}>
              Editar
            </Link>
            {dealer.slug ? (
              <a className="admin-btn admin-btn--ghost" href={`/v/automap/dealers/${dealer.slug}`} target="_blank" rel="noreferrer">
                Ver perfil público
              </a>
            ) : null}
          </>
        }
      />
      <main className="admin-console__main">
        {errorMessage ? <AdminStatePanel variant="empty" message={errorMessage} /> : null}
        {canManage && !dealer.isReadOnly ? (
          <div className="admin-console__header-actions" style={{ marginBottom: "1rem", flexWrap: "wrap" }}>
            <button type="button" className="admin-btn" onClick={() => runAction(dealer.publicProfileEnabled ? "disable_public_profile" : "enable_public_profile")}>
              {dealer.publicProfileEnabled ? "Deshabilitar perfil público" : "Habilitar perfil público"}
            </button>
            <button type="button" className="admin-btn" onClick={() => runAction("mark_verified", { checklist: dealer.verificationChecklist })}>
              Marcar verificado
            </button>
            <button type="button" className="admin-btn" onClick={() => runAction("request_more_info")}>Solicitar info</button>
            <button type="button" className="admin-btn" onClick={() => runAction("start_trial")}>Iniciar trial</button>
            <button type="button" className="admin-btn" onClick={() => runAction("extend_trial", { days: 30 })}>Extender trial</button>
            <button type="button" className="admin-btn" onClick={() => runAction("mark_trial_converted")}>Trial convertido</button>
            <button type="button" className="admin-btn" onClick={() => runAction("pause")}>Pausar</button>
            <button type="button" className="admin-btn" onClick={() => runAction("suspend")}>Suspender</button>
            <button type="button" className="admin-btn" onClick={() => {
              if (window.confirm("¿Archivar este concesionario?")) runAction("archive");
            }}>Archivar</button>
          </div>
        ) : null}
        <DealerDetailNav dealerId={dealerId} />
        <Outlet context={{ dealer, dealerId, canManage, reload, runAction, handleViewAsDealer, portalOpening }} />
      </main>
    </AdminPageLayout>
  );
}

export default function AdminDealerLayout() {
  return (
    <RequirePermission permission={ADMIN_PERMISSIONS.REAL_ESTATE_SOURCES_VIEW}>
      <AdminDealerLayoutContent />
    </RequirePermission>
  );
}
